import { Order, OrderBundleItem, OrderBundleComponent, OrderItems } from '../store/order/api/interface';

const trimGuideValue = (value?: string | null) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const extractGuideText = (value: unknown): string => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return extractGuideText(parsed);
      }
    } catch {
      // plain text guide
    }

    return trimmed;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      trimGuideValue(record.text as string) ||
      trimGuideValue(record.guide as string) ||
      trimGuideValue(record.message as string) ||
      trimGuideValue(record.label as string) ||
      ''
    );
  }

  return trimGuideValue(String(value));
};

/** Per-cake Additional Guide from `order_cake.instruction` */
export const getOrderCakeInstructionForRow = (
  orderItem?: Pick<OrderItems, 'order_cake'> | null
): string => {
  const instruction = extractGuideText(orderItem?.order_cake?.instruction);
  return instruction || '-';
};

/** All cake instructions for order header (comma-separated) */
export const getOrderCakeInstructionsSummary = (
  order?: Pick<Order, 'order_items'> | null
): string => {
  if (!order?.order_items?.length) return '-';

  const instructions = order.order_items
    .map((item) => extractGuideText(item?.order_cake?.instruction))
    .filter(Boolean);

  return instructions.length ? instructions.join(', ') : '-';
};

/** Bundle / order-level Special Request from `specialRequest` or `additionalInfo` */
export const getOrderSpecialRequestText = (
  order?: Pick<Order, 'specialRequest' | 'additionalInfo'> | null
): string => {
  if (!order) return '-';

  const text =
    extractGuideText(order.specialRequest) ||
    extractGuideText((order as { additionalGuide?: unknown }).additionalGuide) ||
    extractGuideText(order.additionalInfo);

  return text || '-';
};

export const getBundleLineTotal = (item: OrderBundleItem) =>
  Number(item?.subTotal) || Number(item?.qty) * Number(item?.unitPrice);

export const formatBundleRowPrice = (
  subTotal?: number | string | null,
  unitPrice?: number | string | null
) => {
  const value = Number(subTotal ?? unitPrice ?? 0);
  if (!value) {
    return '-';
  }
  return parseFloat(value.toFixed(2));
};

export const getBundleChildrenPriceSum = (item: OrderBundleItem) => {
  let sum = 0;

  item.components?.forEach((component) => {
    sum += Number(component.subTotal ?? component.unitPrice ?? 0);
  });

  item.variantSelections?.forEach((selection) => {
    sum += Number(selection.subTotal ?? selection.price ?? 0);
  });

  return parseFloat(sum.toFixed(2));
};

export const bundleUsesOverridePricing = (item: OrderBundleItem) => {
  const bundleTotal = getBundleLineTotal(item);
  const childrenSum = getBundleChildrenPriceSum(item);

  if (!childrenSum) {
    return false;
  }

  return Math.abs(bundleTotal - childrenSum) > 0.009;
};

export const sumBundleItems = (order: Order) => {
  let sum = 0;
  order?.order_bundle_items?.forEach((item) => {
    sum += getBundleLineTotal(item);
  });
  if (Math.round(sum) !== sum) {
    sum = parseFloat(sum.toFixed(2));
  }
  return sum;
};

export const sumOrderItems = (order: Order) => {
  let sum = 0;

  order?.order_items?.forEach((item) => {
    sum += Number(item?.qty) * Number(item?.unitPrice);
  });

  sum += sumBundleItems(order);
  sum += getOrderPackagingTotal(order);

  if (Math.round(sum) !== sum) {
    sum = parseFloat(sum.toFixed(2));
  }

  return sum;
};

export const formatBundleProductVariantSummary = (order: Order) =>
  order?.order_bundle_items
    ?.map((item) => item.labelEn)
    .filter(Boolean)
    .join(', ') || '';

/** @deprecated Use formatBundleProductVariantSummary for the Product Variant column */
export const formatBundleVariantsSummary = (order: Order) =>
  formatBundleProductVariantSummary(order);

export const formatBundleComponentsSummary = (order: Order) =>
  order?.order_bundle_items
    ?.flatMap((item) =>
      item.components
        ?.filter((component) => component.componentType !== 'cake')
        .map((component) => component.labelEn) || []
    )
    .filter(Boolean)
    .join(', ') || '';

export type OrderBundleCakeEntry = {
  bundleItem: OrderBundleItem;
  component: OrderBundleComponent;
};

export const getOrderBundleCakeComponents = (order?: Order): OrderBundleCakeEntry[] => {
  if (!order?.order_bundle_items?.length) {
    return [];
  }

  return order.order_bundle_items.flatMap((bundleItem) =>
    (bundleItem.components || [])
      .filter((component) => component.componentType === 'cake')
      .map((component) => ({ bundleItem, component }))
  );
};

export const getOrderCakeBox = (orderCake?: any) =>
  orderCake?.cakeBox || orderCake?.cake_box || null;

export const getCakeBoxUnitPrice = (orderCake?: any) =>
  parseFloat(Number(getOrderCakeBox(orderCake)?.price || 0).toFixed(2));

export const getCakeUnitPrice = (orderItemUnitPrice: number | string) =>
  parseFloat(Number(orderItemUnitPrice || 0).toFixed(2));

export const getCakeDisplayUnitPrice = (
  orderItemUnitPrice: number | string,
  orderCake?: any
) =>
  parseFloat(
    (getCakeUnitPrice(orderItemUnitPrice) + getCakeBoxUnitPrice(orderCake)).toFixed(
      2
    )
  );

export const getCakeRowDisplayTotal = (
  orderItemQty: number | string,
  orderItemUnitPrice: number | string,
  orderCake?: any
) => {
  const cakeTotal =
    Number(orderItemQty) * getCakeDisplayUnitPrice(orderItemUnitPrice, orderCake);
  return parseFloat(Number(cakeTotal).toFixed(2));
};

export const getOrderPackagingLines = (order?: Order | null) => {
  const lines = (order?.order_items || [])
    .filter((item) => item.order_cake)
    .map((item, index) => {
      const cakeBox = getOrderCakeBox(item.order_cake);
      if (!cakeBox) return null;
      const qty = Number(item.qty || 1);
      return {
        cakeIndex: index + 1,
        labelEn: cakeBox.labelEn,
        price: Number(cakeBox.price || 0) * qty,
      };
    })
    .filter(Boolean) as Array<{
    cakeIndex: number;
    labelEn: string;
    price: number;
  }>;

  return lines;
};

export const getOrderPackagingTotal = (order?: Order | null) =>
  parseFloat(
    getOrderPackagingLines(order)
      .reduce((sum, line) => sum + Number(line.price || 0), 0)
      .toFixed(2)
  );
